import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../contexts/TranslationContext';

const Countries = () => {
  const { t, getCountryData } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Refs for scrollbar functionality
  const modalScrollContainerRef = useRef(null);
  const customScrollbarRef = useRef(null);
  const scrollbarThumbRef = useRef(null);

  const countries = [
    {
      id: 'italy',
      nameKey: 'country-italy-name',
      descKey: 'country-italy-desc',
      flag: 'https://flagcdn.com/w160/it.png'
    },
    {
      id: 'slovenia',
      nameKey: 'country-slovenia-name',
      descKey: 'country-slovenia-desc',
      flag: 'https://flagcdn.com/w160/si.png'
    },
    {
      id: 'macedonia',
      nameKey: 'country-macedonia-name',
      descKey: 'country-macedonia-desc',
      flag: 'https://flagcdn.com/w160/mk.png'
    },
    {
      id: 'serbia',
      nameKey: 'country-serbia-name',
      descKey: 'country-serbia-desc',
      flag: 'https://flagcdn.com/w160/rs.png'
    },
    {
      id: 'bosnia',
      nameKey: 'country-bosnia-name',
      descKey: 'country-bosnia-desc',
      flag: 'https://flagcdn.com/w160/ba.png'
    },
    {
      id: 'bulgaria',
      nameKey: 'country-bulgaria-name',
      descKey: 'country-bulgaria-desc',
      flag: 'https://flagcdn.com/w160/bg.png'
    },
    {
      id: 'croatia',
      nameKey: 'country-croatia-name',
      descKey: 'country-croatia-desc',
      flag: 'https://flagcdn.com/w160/hr.png'
    },
    {
      id: 'usa',
      nameKey: 'country-usa-name',
      descKey: 'country-usa-desc',
      flag: 'https://flagcdn.com/w160/us.png'
    },
    {
      id: 'uk',
      nameKey: 'country-uk-name',
      descKey: 'country-uk-desc',
      flag: 'https://flagcdn.com/w160/gb.png'
    },
    {
      id: 'greece',
      nameKey: 'country-greece-name',
      descKey: 'country-greece-desc',
      flag: 'https://flagcdn.com/w160/gr.png'
    },
    {
      id: 'uae',
      nameKey: 'country-uae-name',
      descKey: 'country-uae-desc',
      flag: 'https://flagcdn.com/w160/ae.png'
    },
    {
      id: 'singapore',
      nameKey: 'country-singapore-name',
      descKey: 'country-singapore-desc',
      flag: 'https://flagcdn.com/w160/sg.png'
    },
    {
      id: 'cyprus',
      nameKey: 'country-cyprus-name',
      descKey: 'country-cyprus-desc',
      flag: 'https://flagcdn.com/w160/cy.png'
    },
    {
      id: 'montenegro',
      nameKey: 'country-montenegro-name',
      descKey: 'country-montenegro-desc',
      flag: 'https://flagcdn.com/w160/me.png'
    },
    {
      id: 'worldwide',
      nameKey: 'country-worldwide-name',
      descKey: 'country-worldwide-desc',
      flag: null,
      special: true
    }
  ];

  const openModal = (country) => {
    setSelectedCountry(country);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCountry(null);
    document.body.style.overflow = 'auto';
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    closeModal();
  };

  // Get detailed country data for the selected country
  const countryDetails = selectedCountry ? getCountryData(selectedCountry.id) : null;

  // Custom scrollbar functionality
  useEffect(() => {
    if (!isModalOpen || !modalScrollContainerRef.current || !customScrollbarRef.current || !scrollbarThumbRef.current) {
      return;
    }

    const modalScrollContainer = modalScrollContainerRef.current;
    const customScrollbar = customScrollbarRef.current;
    const scrollbarThumb = scrollbarThumbRef.current;

    function updateScrollbar() {
      const scrollHeight = modalScrollContainer.scrollHeight;
      const clientHeight = modalScrollContainer.clientHeight;
      const scrollTop = modalScrollContainer.scrollTop;

      // Calculate thumb height as a proportion of visible content
      const thumbHeight = Math.max((clientHeight / scrollHeight) * 100, 10);

      // Calculate thumb position
      const maxScroll = scrollHeight - clientHeight;
      const scrollPercentage = maxScroll > 0 ? scrollTop / maxScroll : 0;
      const thumbTop = scrollPercentage * (100 - thumbHeight);

      // Update thumb styles
      scrollbarThumb.style.height = thumbHeight + '%';
      scrollbarThumb.style.top = thumbTop + '%';

      // Show/hide scrollbar based on content overflow
      if (scrollHeight > clientHeight) {
        customScrollbar.classList.add('scrollbar-visible');
      } else {
        customScrollbar.classList.remove('scrollbar-visible');
      }
    }

    // Dragging functionality
    let isDragging = false;
    let startY = 0;
    let startScrollTop = 0;

    const handleMouseDown = (e) => {
      isDragging = true;
      startY = e.clientY;
      startScrollTop = modalScrollContainer.scrollTop;
      document.body.style.userSelect = 'none';
      scrollbarThumb.style.background = 'linear-gradient(135deg, #5a6fa3 0%, #6684a3 100%)';
      e.preventDefault();
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const deltaY = e.clientY - startY;
      const scrollbarHeight = customScrollbar.clientHeight;
      const thumbHeight = scrollbarThumb.clientHeight;
      const maxThumbTop = scrollbarHeight - thumbHeight;

      const scrollRatio = deltaY / maxThumbTop;
      const maxScroll = modalScrollContainer.scrollHeight - modalScrollContainer.clientHeight;
      const newScrollTop = startScrollTop + (scrollRatio * maxScroll);

      modalScrollContainer.scrollTop = Math.max(0, Math.min(newScrollTop, maxScroll));
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        document.body.style.userSelect = '';
        scrollbarThumb.style.background = 'linear-gradient(135deg, #677DB7 0%, #7392B7 100%)';
      }
    };

    // Event listeners
    modalScrollContainer.addEventListener('scroll', updateScrollbar);
    scrollbarThumb.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // ResizeObserver for content changes
    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(modalScrollContainer);

    // Initialize scrollbar
    setTimeout(updateScrollbar, 100);

    // Cleanup
    return () => {
      modalScrollContainer.removeEventListener('scroll', updateScrollbar);
      scrollbarThumb.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      resizeObserver.disconnect();
    };
  }, [isModalOpen]);

  return (
    <>
      <section className="countries">
        <div className="container">
          <div className="section-header">
            <h2>{t('countries-title')}</h2>
            <p>{t('countries-subtitle')}</p>
          </div>
          <div className="countries-grid">
            {countries.map((country, index) => (
              <div 
                key={country.id} 
                className={`country-card ${country.special ? 'special' : ''}`}
                data-country={country.id}
                data-special={country.special || undefined}
                style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                onClick={() => openModal(country)}
              >
                <div className="country-flag">
                  {country.flag ? (
                    <img src={country.flag} alt={t(country.nameKey)} className="flag-image" />
                  ) : (
                    <div className="worldwide-icon">
                      <i className="fas fa-globe"></i>
                    </div>
                  )}
                </div>
                <h3>{t(country.nameKey)}</h3>
                <p>{t(country.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Country Modal */}
      {isModalOpen && selectedCountry && countryDetails && (
        <div className={`modal ${isModalOpen ? 'active' : ''}`} onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-scroll-container" ref={modalScrollContainerRef}>
              <div className="modal-header">
                <div className="modal-flag">
                  {selectedCountry.flag ? (
                    <img src={selectedCountry.flag} alt={countryDetails.name} className="modal-flag-image" />
                  ) : (
                    <div className="worldwide-icon">
                      <i className="fas fa-globe"></i>
                    </div>
                  )}
                </div>
                <h2>{countryDetails.name}</h2>
                <span className="country-code">{countryDetails.code}</span>
              </div>
              
              <div className="modal-details">
                {/* Overview Section */}
                <div className="detail-section">
                  <h3>{t('modal-overview')}</h3>
                  <p>{countryDetails.overview}</p>
                </div>

                {/* Advantages Section */}
                <div className="detail-section">
                  <h3>{t('modal-advantages')}</h3>
                  <ul className="detail-list">
                    {countryDetails.advantages.map((advantage, index) => (
                      <li key={index}>
                        <i className="fas fa-check"></i>
                        {advantage}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Requirements Section */}
                <div className="detail-section">
                  <h3>{t('modal-requirements')}</h3>
                  <ul className="detail-list">
                    {countryDetails.requirements.map((requirement, index) => (
                      <li key={index}>
                        <i className="fas fa-info-circle"></i>
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Formation Details Section */}
                <div className="detail-section">
                  <h3>{t('modal-formation-details')}</h3>
                  <p><strong>{t('modal-timeframe')}:</strong> {countryDetails.timeframe}</p>
                  <p><strong>{t('modal-taxation')}:</strong> {countryDetails.taxation}</p>
                </div>

                {/* Special Notice for Worldwide */}
                {selectedCountry.special && countryDetails.special && (
                  <div className="detail-section special-notice">
                    <h3><i className="fas fa-star"></i> {t('modal-special-notice')}</h3>
                    <div className="notice-content">
                      <p><strong>{countryDetails.special}</strong></p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="modal-cta">
                <button className="btn btn-primary" onClick={scrollToContact}>
                  {t('modal-book-consultation')}
                </button>
                <button className="btn btn-secondary modal-close" onClick={closeModal}>
                  &times;
                </button>
              </div>
            </div>
            <div className="custom-scrollbar" ref={customScrollbarRef}>
              <div className="scrollbar-thumb" ref={scrollbarThumbRef}></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Countries; 